import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Platform } from '@ionic/angular';
import { Share } from '@capacitor/share';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { DeviceService, DeviceInfo } from './device.service';
import { getOrCreateDebugSessionId } from './startup-debug.util';

export interface LogEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error' | 'debug';
  tag: string; // Renamed from module for consistency
  message: string;
  data?: any;
}

export interface SystemLogEntry {
  timestamp: string;
  pid: string;
  tid: string;
  level: string;
  tag: string;
  message: string;
  raw: string;
}

interface DebugManifestConfig {
  debugLogEndpoint?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DebugLoggerService {
  private logs: LogEntry[] = [];
  private systemLogs: SystemLogEntry[] = [];
  private maxLogs = 1000; // Maximum number of logs to keep in memory
  private maxSystemLogs = 500; // Maximum number of system logs to keep
  private debugEnabled = false;
  private isDebugVariant = false;
  private appIdentifier = 'unknown';
  private appVersion = 'unknown';
  private remoteEndpoint = '';
  private remoteUploadEnabled = false;
  private readonly endpointStorageKey = 'aforo_debug_endpoint';
  private readonly endpointManifestUrl = 'https://aforopro.bink.tools/version.json';
  private readonly fallbackEndpoint = 'https://ws.aforo.bink.tools:8080/api/debug/logs';
  private readonly startupDebugFlagKey = 'aforo_startup_debug';
  private deviceContext: DeviceInfo | null = null;
  private userContext: { userName?: string; userDisplayName?: string } = {};
  private sessionId: string | null = null;

  // Observable for logs
  private logsSubject = new BehaviorSubject<LogEntry[]>([]);
  public logs$ = this.logsSubject.asObservable();

  // Observable for system logs
  private systemLogsSubject = new BehaviorSubject<SystemLogEntry[]>([]);
  public systemLogs$ = this.systemLogsSubject.asObservable();

  constructor(
    private platform: Platform,
    private deviceService: DeviceService
  ) {
    // Initialize with timestamp to mark app start
    this.initializeRemoteLogger();
    this.log('DebugLogger', 'Debug logger initialized', {
      platform: platform.platforms(),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Enable or disable debug logging
   */
  public setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
    this.log('DebugLogger', `Debug logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Log an informational message
   */
  public log(tag: string, message: string, data?: any): void {
    this.addLogEntry('info', tag, message, data);
  }

  /**
   * Log a warning message
   */
  public warn(tag: string, message: string, data?: any): void {
    this.addLogEntry('warn', tag, message, data);
  }

  /**
   * Log an error message
   */
  public error(tag: string, message: string, data?: any): void {
    this.addLogEntry('error', tag, message, data);
  }

  /**
   * Log a debug message (only if debug is enabled)
   */
  public debug(tag: string, message: string, data?: any): void {
    this.addLogEntry('debug', tag, message, data);
  }

  /**
   * Clear all logs
   */
  public clearLogs(): void {
    this.logs = [];
    this.systemLogs = [];
    this.logsSubject.next(this.logs);
    this.systemLogsSubject.next(this.systemLogs);
    this.log('DebugLogger', 'Logs cleared');
  }

  /**
   * Export logs as JSON
   */
  public async exportLogs(): Promise<void> {
    try {
      const logData = JSON.stringify({
        appLogs: this.logs,
        systemLogs: this.systemLogs
      }, null, 2);

      const timestamp = new Date().toISOString().replace(/:/g, '-');
      const fileName = `aforo-debug-logs-${timestamp}.json`;

      // Save to file
      const result = await Filesystem.writeFile({
        path: fileName,
        data: logData,
        directory: Directory.Documents,
        encoding: Encoding.UTF8
      });

      this.log('DebugLogger', `Logs exported to ${result.uri}`);

      // Share the file
      if (this.platform.is('capacitor')) {
        await Share.share({
          title: 'Aforo Debug Logs',
          text: 'Debug logs from Aforo app',
          url: result.uri,
          dialogTitle: 'Share debug logs'
        });
      }
    } catch (error) {
      this.error('DebugLogger', 'Failed to export logs', error);
      console.error('Failed to export logs:', error);
    }
  }

  /**
   * Format logs as text for sharing
   */
  public formatLogsAsText(filter?: string): string {
    let appLogText = this.formatAppLogsAsText(filter);
    // No system logs in the default output unless specifically requested

    return appLogText;
  }

  /**
   * Format app logs as text for sharing
   */
  private formatAppLogsAsText(filter?: string): string {
    let filteredLogs = this.logs;

    if (filter) {
      // Support regex-like OR filtering using pipe character
      if (filter.includes('|')) {
        const filterTerms = filter.split('|').map(term => term.trim().toLowerCase());
        filteredLogs = this.logs.filter(log =>
          filterTerms.some(term =>
            (log.tag && log.tag.toLowerCase().includes(term)) ||
            (log.message && log.message.toLowerCase().includes(term))
          )
        );
      } else {
        filteredLogs = this.logs.filter(log =>
          (log.tag && log.tag.toLowerCase().includes(filter.toLowerCase())) ||
          (log.message && log.message.toLowerCase().includes(filter.toLowerCase()))
        );
      }
    }

    return filteredLogs.map(log => {
      const timestamp = new Date(log.timestamp).toISOString();
      const data = log.data ? `\n${this.formatLogData(log.data)}` : '';
      return `[${timestamp}] [${log.level.toUpperCase()}] [${log.tag}] ${log.message}${data}`;
    }).join('\n\n');
  }

  /**
   * Format system logs as text for sharing
   */
  public formatSystemLogsAsText(filter?: string): string {
    let filteredLogs = this.systemLogs;

    if (filter) {
      // Support regex-like OR filtering using pipe character
      if (filter.includes('|')) {
        const filterTerms = filter.split('|').map(term => term.trim().toLowerCase());
        filteredLogs = this.systemLogs.filter(log =>
          filterTerms.some(term =>
            (log.tag && log.tag.toLowerCase().includes(term)) ||
            (log.message && log.message.toLowerCase().includes(term))
          )
        );
      } else {
        filteredLogs = this.systemLogs.filter(log =>
          (log.tag && log.tag.toLowerCase().includes(filter.toLowerCase())) ||
          (log.message && log.message.toLowerCase().includes(filter.toLowerCase()))
        );
      }
    }

    return filteredLogs.map(log => log.raw).join('\n');
  }

  /**
   * Format log data for display
   */
  private formatLogData(data: any): string {
    if (!data) return '';

    try {
      if (typeof data === 'string') {
        return data;
      } else {
        return JSON.stringify(data, null, 2);
      }
    } catch (e) {
      return String(data);
    }
  }

  /**
   * Get all logs
   */
  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get all system logs
   */
  public getSystemLogs(): SystemLogEntry[] {
    return [...this.systemLogs];
  }

  /**
   * Filter logs by tag
   */
  public getLogsByTag(tag: string): LogEntry[] {
    return this.logs.filter(log => log.tag === tag);
  }

  /**
   * Filter logs by level
   */
  public getLogsByLevel(level: 'info' | 'warn' | 'error' | 'debug'): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Parse and add ADB logcat output
   */
  public parseLogcatOutput(logcatOutput: string): void {
    if (!logcatOutput) return;

    const lines = logcatOutput.split('\n');

    for (const line of lines) {
      if (!line.trim()) continue;

      try {
        // Parse logcat line format: MM-DD HH:MM:SS.SSS PID TID LEVEL TAG: MESSAGE
        const match = line.match(/(\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}\.\d{3})\s+(\d+)\s+(\d+)\s+([VDIWEF])\s+([^:]+):\s+(.*)/);

        if (match) {
          const [, timestamp, pid, tid, level, tag, message] = match;

          const systemLogEntry: SystemLogEntry = {
            timestamp,
            pid,
            tid,
            level,
            tag: tag.trim(),
            message,
            raw: line
          };

          // Add to system logs array
          this.systemLogs.push(systemLogEntry);

          // Trim logs if they exceed the maximum
          if (this.systemLogs.length > this.maxSystemLogs) {
            this.systemLogs = this.systemLogs.slice(-this.maxSystemLogs);
          }
        } else {
          // If it doesn't match the pattern, just add it as a raw log
          this.systemLogs.push({
            timestamp: new Date().toISOString(),
            pid: '0',
            tid: '0',
            level: 'I',
            tag: 'LogParser',
            message: line,
            raw: line
          });
        }
      } catch (error) {
        console.error('Error parsing logcat line:', error);
      }
    }

    // Update the observable
    this.systemLogsSubject.next(this.systemLogs);
  }

  /**
   * Add a log entry
   */
  private addLogEntry(level: 'info' | 'warn' | 'error' | 'debug', tag: string, message: string, data?: any): void {
    // Skip all logging unless debug is enabled and it's an error
    if (!this.debugEnabled && level !== 'error') {
      return;
    }

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      tag,
      message,
      data
    };

    // Add to logs array
    this.logs.push(entry);

    // Trim logs if they exceed the maximum
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Update the observable
    this.logsSubject.next(this.logs);
    this.uploadLogEntry(entry);

    const serializedData = data != null ? this.safeStringify(data) : '';

    if (!this.debugEnabled && level === 'error') {
      console.error(`[${tag}] ${message}`, serializedData);
    } else if (this.debugEnabled) {
      switch (level) {
        case 'info':
          console.log(`[${tag}] ${message}`, serializedData);
          break;
        case 'warn':
          console.warn(`[${tag}] ${message}`, serializedData);
          break;
        case 'error':
          console.error(`[${tag}] ${message}`, serializedData);
          break;
        case 'debug':
          console.debug(`[${tag}] ${message}`, serializedData);
          break;
      }
    }
  }

  private async initializeRemoteLogger(): Promise<void> {
    try {
      const debugSwitchEnabled = this.isDebugSwitchEnabled();
      if (!Capacitor.isNativePlatform() && !debugSwitchEnabled) return;

      const appInfo = Capacitor.isNativePlatform() ? await App.getInfo().catch(() => null) : null;

      this.appIdentifier = appInfo?.id || 'unknown';
      this.appVersion = appInfo?.version || 'unknown';
      this.isDebugVariant = this.appIdentifier.endsWith('.debug') || this.appVersion.toLowerCase().includes('debug');
      const debugEnabledByPolicy = debugSwitchEnabled;
      this.debugEnabled = this.debugEnabled || debugEnabledByPolicy;
      if (!debugEnabledByPolicy) {
        this.remoteUploadEnabled = false;
        return;
      }

      const [deviceInfo, manifestConfig] = await Promise.all([
        this.deviceService.getDeviceInfo().catch(() => null),
        this.fetchManifestConfig()
      ]);

      this.deviceContext = deviceInfo;
      this.userContext = {
        userName: deviceInfo?.userName,
        userDisplayName: deviceInfo?.userDisplayName
      };

      const localEndpoint = localStorage.getItem(this.endpointStorageKey) || '';
      this.remoteEndpoint = localEndpoint || manifestConfig.debugLogEndpoint || this.fallbackEndpoint;
      this.remoteUploadEnabled = !!this.remoteEndpoint;
      if (this.remoteUploadEnabled && !this.sessionId) {
        this.sessionId = getOrCreateDebugSessionId();
      }
    } catch (error) {
      console.warn('[DebugLogger] Failed to initialize remote logger', error);
    }
  }

  private safeStringify(value: unknown): string {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private isDebugSwitchEnabled(): boolean {
    try {
      return localStorage.getItem(this.startupDebugFlagKey) === '1';
    } catch {
      return false;
    }
  }

  private async fetchManifestConfig(): Promise<DebugManifestConfig> {
    try {
      const response = await fetch(this.endpointManifestUrl, { method: 'GET' });
      if (!response.ok) {
        return {};
      }
      const manifest = await response.json() as DebugManifestConfig;
      return manifest || {};
    } catch {
      return {};
    }
  }

  private uploadLogEntry(entry: LogEntry): void {
    if (!this.remoteUploadEnabled || !this.remoteEndpoint) {
      return;
    }

    const payload = {
      app: {
        appId: this.appIdentifier,
        appVersion: this.appVersion,
        platform: Capacitor.getPlatform(),
        debugVariant: this.isDebugVariant
      },
      session: {
        sessionId: this.sessionId || getOrCreateDebugSessionId()
      },
      device: {
        deviceId: this.deviceContext?.deviceId || null,
        deviceName: this.deviceContext?.deviceName || null,
        userName: this.userContext.userName || null,
        userDisplayName: this.userContext.userDisplayName || null,
        os: this.deviceContext?.operatingSystem || null,
        osVersion: this.deviceContext?.osVersion || null,
        model: this.deviceContext?.model || null
      },
      log: {
        timestamp: entry.timestamp,
        level: entry.level,
        tag: entry.tag,
        message: entry.message,
        data: this.redactSensitiveData(entry.data)
      }
    };

    fetch(this.remoteEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch(() => {});
  }

  private redactSensitiveData(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }
    if (typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.redactSensitiveData(item));
    }

    const clone: Record<string, any> = {};
    for (const key of Object.keys(value)) {
      const lower = key.toLowerCase();
      const shouldRedact =
        lower.includes('token') ||
        lower.includes('password') ||
        lower.includes('secret') ||
        lower.includes('authorization') ||
        lower === 'key';
      clone[key] = shouldRedact ? '[REDACTED]' : this.redactSensitiveData(value[key]);
    }
    return clone;
  }
} 